import axios, { AxiosResponse } from 'axios'
import { AddonParamData, AddonProduct, AddonProductEnum, BaseAddressDto, FindAddressResult, InsurerWithPaymentFrequencies, Item, SimpleItem } from 'BrokerTsLib/src/dtos'
import { Product, Risk, SelectionType } from 'brokertslib/src/enums'
import { Enums, getEnumName, longStaleTime } from 'brokertslib/src/hooks'
import { UseQueryResult, UseQueryOptions, useQuery, useMutation } from 'react-query'
import { useAuth } from '../context/AuthContext'
import { useApp } from './useApp'

export const baseUrl = process.env.PUBLIC_URL
const apiUrl = process.env.REACT_APP_ENUM_API
const apiTravelUrl = process.env.REACT_APP_ENUM_TRAVEL_API
const apiPropertyUrl = process.env.REACT_APP_ENUM_PROPERTY_API
const addonEnumApiUrl = process.env.REACT_APP_ADDON_ENUM_API

export const useUrl = () => {
    const { product } = useApp()

    switch (product) {
        case Product.TravelInsurance:
            return `${baseUrl}/${apiTravelUrl}`
        case Product.PropertyInsurance:
            return `${baseUrl}/${apiPropertyUrl}`
        default:
            return `${baseUrl}/${apiUrl}`
    }
}

export type GenericQueryOptions<T> = Omit<UseQueryOptions<AxiosResponse<T, any>, any, AxiosResponse<T, any>, any>, "queryKey" | "queryFn"> | undefined

// wrapovani hooks z knihovny brokerTsLib aby pri pouziti dale v kodu byly silne typove

export const useSelections = (selectionType?: SelectionType, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> => {
    const url = useUrl()

    return useQuery([getEnumName(Enums.selection), selectionType],
        () => axios.get<Item[]>(`${url}/GetSelections?type=${selectionType}`),
        { staleTime: longStaleTime, ...options })
}

export const useInsurers = (addonOnly: boolean | null, includeInvalid: boolean | null, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> => {
    const url = useUrl()

    return useQuery([getEnumName(Enums.insurer), addonOnly, includeInvalid, url],
        () => axios.get<Item[]>(`${url}/GetInsurers?addonProviders=${addonOnly}&includeInvalid=${includeInvalid}`),
        { staleTime: longStaleTime, ...options })
}

export const useInsurerPaymentFrequencies = (options?: GenericQueryOptions<InsurerWithPaymentFrequencies[]>): UseQueryResult<AxiosResponse<InsurerWithPaymentFrequencies[], any>, any> => {
    const url = useUrl()

    return useQuery(['InsurerWithPaymentFrequencies', url],
        () => axios.get<InsurerWithPaymentFrequencies[]>(`${url}/GetInsurerWithAllowedPaymentFrequencies`),
        { staleTime: longStaleTime, ...options })
}

// export const useCountries = (enabled?: boolean, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> =>
//     libHooks.useCountries(enabled, RequiredAuthOpts(options))

export const useCountries = (enabled?: boolean, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
    const url = useUrl()

    return useQuery(getEnumName(Enums.country),
        () => axios.get<SimpleItem[]>(`${url}/GetCountries`),
        RequiredAuthOpts({ staleTime: longStaleTime, enabled, ...options }))
}


// export const useRegions = (postCode: string, options?: GenericQueryOptions<BaseAddressDto[]>): UseQueryResult<AxiosResponse<BaseAddressDto[], any>, any> =>
//     libHooks.useRegions(postCode, RequiredAuthOpts(options))

export const useRegions = (postCode: string, options?: GenericQueryOptions<BaseAddressDto[]>): UseQueryResult<AxiosResponse<BaseAddressDto[], any>, any> => {
    const url = useUrl()

    return useQuery([getEnumName(Enums.region), postCode],
        () => axios.get<BaseAddressDto[]>(`${url}/GetRegions?postCode=${postCode}`),
        RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

// export const useGetRiskVariants = (risk?: Risk, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> =>
//     libHooks.useGetRiskVariants(risk, RequiredAuthOpts(options))

export const useGetRiskVariants = (risk?: Risk, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> => {
    const url = useUrl()

    return useQuery([getEnumName(Enums.riskVariant), risk], () => axios.get<Item[]>(`${url}/GetRiskVariants?riskId=${risk}`), RequiredAuthOpts({
        staleTime: longStaleTime, ...options
    }))
}

// export const useYearKm = (categoryId: number, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> =>
//     libHooks.useYearKm(categoryId, RequiredAuthOpts(options))

export const useYearKm = (categoryId: number, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
    const url = useUrl()

    return useQuery(['useYearKm', categoryId],
        () => axios.get<SimpleItem[]>(`${url}/GetYearKm?categoryId=${categoryId}`),
        RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

export const useGetAddonProductEnums = () =>
    useMutation((data: { offerId: number, riskVariantId: number, changedParams: AddonParamData[], filterOutput: boolean }) =>
        axios.post<AddonProductEnum[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonProductEnums`, data))

export const useGetAddonRiskVariantProducts = () =>
    useMutation((data: { offerId: number, riskVariantId: number }) =>
        axios.post<SimpleItem[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonRiskVariantProducts?offerId=${data.offerId}&riskVariantId=${data.riskVariantId}`))

export const useGetAddonParamValues = () =>
    useMutation((data: { mainOfferId: number, addonOfferId: number, paramName: string }) =>
        axios.get<AddonProduct[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonParamValues?mainOfferId=${data.mainOfferId}&addonOfferId=${data.addonOfferId}&paramName=${data.paramName}`))

export const useFindStreet = (search: string, options?: GenericQueryOptions<string[]>): UseQueryResult<AxiosResponse<string[], any>, any> => {
    const url = useUrl()

    return useQuery(['useFindStreet', search],
        () => axios.get<string[]>(`${url}/FindStreet?search=${search}`),
        RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

export const useFindAddress = (street: string, searchQuery: string, options?: GenericQueryOptions<FindAddressResult[]>): UseQueryResult<AxiosResponse<FindAddressResult[], any>, any> => {
    const url = useUrl()

    return useQuery(['useFindAddress', street, searchQuery],
        () => axios.get<FindAddressResult[]>(`${url}/FindAddress?street=${street}&searchQuery=${searchQuery}`),
        RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

export const useFindKatUzemi = (zipCode: string, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
    const url = useUrl()

    return useQuery(['useFindKatUzemi', zipCode],
        () => axios.get<SimpleItem[]>(`${url}/FindKatUzemi?psc=${zipCode}`),
        RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

const RequiredAuthOpts = <T>(opt: GenericQueryOptions<T>): GenericQueryOptions<T> => {
    var ctx = useAuth()

    return { ...opt, enabled: opt?.enabled && ctx?.user?.logged }
}